import { ActivatedRoute, Data, Params } from '@angular/router';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Combines the Route Parameters and Query Parameters into a single observable.
 *
 * @example
 * ```js
 * // Given the route definition
 * // /users/:id
 * // And the current url:
 * // /users/987?orgId=123
 * getCombinedParameters(this.route).subscribe(
 * (params: Params) => {
 *   //...
 *   params['id']; // 987
 *   params['orgId']; //123
 * }
 * ```
 *
 * Note: Route parameters are treated as more important than query parameters
 * so if you have similarly named params you'll get the route param under params['name'].
 *
 * @param activatedRoute The activated route
 * @return a param map of both route parameters and query parameters
 */
export function getCombinedParameters(activatedRoute: ActivatedRoute): Observable<Params | Data> {
  if (!activatedRoute) return of({});

  return combineLatest([
    activatedRoute.params,
    activatedRoute.queryParams,
    activatedRoute.data,
  ]).pipe(
    map(([routeParams, queryParams, data]) => ({
      ...data,
      ...queryParams,
      ...routeParams,
    })),
  );
}
